import { IconSave } from '@maia/icons';
import { AddressDto } from '@maia/shared';
import { Button, getErrors, showToast } from '@maia/ui';
import { formatToDateInput } from '@maia/utils';
import { addMonths, subMonths } from 'date-fns';
import { useEffect } from 'react';
import * as v from 'valibot';
import { usePostalCodeLookup } from '../../hooks/usePostalCodeLookup';
import { useAppForm } from '../form/dynamic-form/dynamic-form';

type EditContactFormProps = {
  customerGuid: string;
  address: AddressDto;
  onSubmit: (value: {
    customerGuid: string;
    body: { effectiveDate: string; address: AddressDto };
  }) => void;
  isPending: boolean;
};

export function EditAddressForm(props: EditContactFormProps) {
  const { customerGuid, address, onSubmit, isPending } = props;

  const currentDate = new Date();

  const form = useAppForm({
    defaultValues: {
      effectiveDate: new Date(),
      address: {
        postalCode: address.postalCode ?? '',
        number: address.number ?? undefined,
        appendix: address.appendix ?? '',
        street: address.street ?? '',
        city: address.city ?? '',
        country: 'nl',
      } as AddressDto,
      statement: false,
    },
    onSubmit: ({ value }) =>
      onSubmit({
        customerGuid,
        body: {
          effectiveDate: formatToDateInput(value.effectiveDate),
          address: value.address,
        },
      }),
  });

  const postalCodeLookup = usePostalCodeLookup({
    onSuccess: (address) => {
      if (address.success && address.response) {
        form.setFieldValue('address.street', address.response.street);
        form.setFieldValue('address.city', address.response.city);
        form.validateField('address.street', 'change');
        form.validateField('address.city', 'change');
        form.validateField('address', 'change');
      }
    },
    onError: (error: any) => {
      if (error?.status !== 404)
        showToast({
          intent: 'error',
          title: 'Ophalen van adres niet gelukt',
          description:
            'Er ging iets mis bij het ophalen van je adres, probeer het later opnieuw aub',
          config: { position: 'top-right' },
        });

      form.setFieldValue('address.street', '');
      form.setFieldValue('address.city', '');
    },
  });

  useEffect(() => {
    return form.store.subscribe(({ prevVal, currentVal }) => {
      if (
        prevVal.values.address.postalCode !==
          currentVal.values.address.postalCode ||
        prevVal.values.address.number !== currentVal.values.address.number
      ) {
        postalCodeLookup.handleInputChange({
          postalCode: form.store.state.values.address.postalCode,
          number:
            typeof form.store.state.values.address.number === 'string'
              ? parseInt(form.store.state.values.address.number)
              : form.store.state.values.address.number,
        });
      }
    });
  }, [form.store, postalCodeLookup]);

  return (
    <form
      className="grid grid-cols-12 gap-24 col-span-full"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.AppField
        name="effectiveDate"
        validators={{
          onChange: v.pipe(
            v.date('Vul een geldige datum in'),
            v.minValue(
              subMonths(currentDate, 1),
              'De datum mag maximaal een maand in het verleden liggen',
            ),
            v.maxValue(
              addMonths(currentDate, 1),
              'De datum mag maximaal een maand in de toekomst liggen',
            ),
          ),
        }}
      >
        {(field) => (
          <field.DateField
            className="col-span-full"
            label="Wanneer gaat het nieuwe adres in?"
            error={getErrors(field)}
            inputOverrides={{
              fieldSize: 'large',
            }}
          />
        )}
      </form.AppField>
      <form.AppField
        name="address"
        validators={{
          onChange: v.object({
            street: v.pipe(
              v.string('Straat is verplicht'),
              v.minLength(1, 'Straat is verplicht'),
            ),
            number: v.pipe(
              v.number('Huisnummer is verplicht'),
              v.minValue(1, 'Huisnummer is verplicht'),
            ),
            appendix: v.optional(v.string()),
            city: v.pipe(
              v.string('Stad is verplicht'),
              v.minLength(1, 'Stad is verplicht'),
            ),
            postalCode: v.pipe(
              v.string('Postcode is verplicht'),
              v.minLength(6, 'Postcode moet minimaal 6 tekens bevatten'),
            ),
          }),
        }}
      >
        {(field) => {
          return (
            <field.PostcodeLookup
              showAdditionalFields={
                !postalCodeLookup.isIdle &&
                !postalCodeLookup.isPending &&
                !postalCodeLookup.isSuccess
              }
              showFoundAddress={
                !postalCodeLookup.isPending &&
                !postalCodeLookup.isError &&
                postalCodeLookup.isSuccess
              }
              label="Postcode"
              error={getErrors(field)}
            />
          );
        }}
      </form.AppField>
      <form.AppField name="statement">
        {(field) => (
          <field.StatementCheckboxField
            className="col-span-full"
            label="Ik bevestig dat de klant begrijpt dat deze wijziging invloed kan hebben op de premie en dat de verzekering opnieuw wordt beoordeeld."
          />
        )}
      </form.AppField>
      <div className="col-span-full flex justify-end">
        <form.Subscribe
          selector={(state) => {
            return [
              state.canSubmit,
              state.isSubmitting,
              state.isValid,
              state.values,
            ];
          }}
        >
          {([canSubmit, isSubmitting, isValid, values]) => (
            <Button
              type="submit"
              intent="cta"
              iconLeft={IconSave}
              isDisabled={
                !canSubmit ||
                isSubmitting ||
                isPending ||
                !isValid ||
                !values.statement
              }
              isLoading={isPending}
            >
              Wijziging aanvragen
            </Button>
          )}
        </form.Subscribe>
      </div>
    </form>
  );
}
